/**
 * Repository-scan linkage UI (issue #2000, ADR-2000).
 *
 * - {@link ScanLinkBadge}: detail-page badge for a scan-linked Service.
 * - {@link ScanLinkCallToAction}: dismissible nudge on an unlinked Service,
 *   shown to users who can edit Services only.
 * - {@link ScanManagedBanner} / {@link ScanManagedFieldNote}: edit-form
 *   overwrite awareness. Never blocking; fields stay editable.
 */

import { useState } from 'react';
import { usePermission } from '../../hooks/usePermission';
import type { ScanManagedField, Service } from '../../graphql/types';
import {
  SCANNER_ONBOARDING_URL,
  APP_METADATA_PATH,
  appMetadataUrl,
  ctaDismissKey,
  findManagedField,
  isScanLinked,
  managedBannerText,
  managedFieldNote,
  teamChangeWarning,
} from '../../lib/scanManagedFields';
import { safeExternalHref } from '../../lib/safeExternalHref';
import '@tylertech/forge/badge';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './ScanLinkage.css';

const BadgeElement = 'forge-badge' as React.ElementType;
const IconElement = 'forge-icon' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

export interface ScanLinkBadgeProps {
  service: Pick<Service, 'scannerAppId' | 'repo'>;
}

/** "Linked to repository scan" badge with the source repo and its metadata file. */
export function ScanLinkBadge({ service }: ScanLinkBadgeProps): JSX.Element | null {
  if (!isScanLinked(service)) {
    return null;
  }
  const metadataUrl = appMetadataUrl(service.repo);
  // Only an http(s) repo URL becomes a link; anything else (e.g. `javascript:`)
  // is shown as text.
  const repoHref = safeExternalHref(service.repo);
  return (
    <div className="scan-link-badge" data-testid="scan-link-badge">
      <BadgeElement theme="success-secondary">Linked to repository scan</BadgeElement>
      {service.repo && (
        <span className="scan-link-badge__source forge-typography--caption">
          Source:{' '}
          {repoHref ? (
            <a href={repoHref} target="_blank" rel="noopener noreferrer">
              {service.repo}
            </a>
          ) : (
            service.repo
          )}
          {metadataUrl && (
            <>
              {' · '}
              <a href={metadataUrl} target="_blank" rel="noopener noreferrer">
                {APP_METADATA_PATH}
              </a>
            </>
          )}
        </span>
      )}
    </div>
  );
}

function readDismissed(serviceId: string): boolean {
  try {
    return window.localStorage.getItem(ctaDismissKey(serviceId)) === '1';
  } catch {
    return false;
  }
}

export interface ScanLinkCallToActionProps {
  service: Pick<Service, 'entityId' | 'scannerAppId'>;
}

/** Dismissible call to action for an unlinked Service; owners and editors only. */
export function ScanLinkCallToAction({ service }: ScanLinkCallToActionProps): JSX.Element | null {
  const canEdit = usePermission('service:update');
  const [dismissed, setDismissed] = useState(() => readDismissed(service.entityId));

  if (isScanLinked(service) || !canEdit || dismissed) {
    return null;
  }

  const handleDismiss = (): void => {
    try {
      window.localStorage.setItem(ctaDismissKey(service.entityId), '1');
    } catch {
      // Storage unavailable: the dismissal lasts for this view only.
    }
    setDismissed(true);
  };

  return (
    <div className="scan-link-cta" role="note" data-testid="scan-link-cta">
      <IconElement name="link" aria-hidden="true" />
      <p className="forge-typography--body2 scan-link-cta__text">
        This Service isn&apos;t linked to a repository scan. Linking keeps its name, description,
        team, and URL in sync with your repo.{' '}
        <a href={SCANNER_ONBOARDING_URL} target="_blank" rel="noopener noreferrer">
          Learn how to link it
        </a>
      </p>
      <ForgeButton type="button" variant="outlined" onClick={handleDismiss}>
        Dismiss
      </ForgeButton>
    </div>
  );
}

export interface ScanManagedBannerProps {
  repo?: string | null;
}

/** Form-level info banner for a linked Service's edit form. */
export function ScanManagedBanner({ repo }: ScanManagedBannerProps): JSX.Element {
  return (
    <div className="scan-managed-banner" role="note" data-testid="scan-managed-banner">
      <IconElement name="info" aria-hidden="true" />
      <p className="forge-typography--body2 scan-managed-banner__text">{managedBannerText(repo)}</p>
    </div>
  );
}

export interface ScanManagedFieldNoteProps {
  /** The Service's manifest; empty or undefined renders nothing. */
  fields: readonly ScanManagedField[] | null | undefined;
  /** Logical field name, e.g. "name" or "teamId". */
  field: string;
  /** Only meaningful for `teamId`: the user has changed the value from its saved one. */
  changed?: boolean;
}

/** Per-field indicator beneath a scan-managed form field. */
export function ScanManagedFieldNote({
  fields,
  field,
  changed = false,
}: ScanManagedFieldNoteProps): JSX.Element | null {
  const entry = findManagedField(fields, field);
  if (!entry) {
    return null;
  }
  const escalate = field === 'teamId' && changed;
  return (
    <>
      <p
        className="scan-managed-note forge-typography--caption"
        data-testid={`scan-managed-note-${field}`}
      >
        <IconElement name="link" aria-hidden="true" />
        <span>{managedFieldNote(entry)}</span>
      </p>
      {escalate && (
        <p
          className="scan-managed-warning forge-typography--body2"
          role="alert"
          data-testid="scan-managed-team-warning"
        >
          <IconElement name="warning" aria-hidden="true" />
          <span>{teamChangeWarning(entry)}</span>
        </p>
      )}
    </>
  );
}
